import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Minus, Scale, CheckCircle2, AlertTriangle } from 'lucide-react';
import { DropdownItemOption } from './ItemDropdown';
import { ItemDropdown } from './ItemDropdown';
import { UnitDropdown } from './UnitDropdown';
import { InventoryBalanceItem } from '../../types';
import { tg } from '../../telegram';
import { formatQty, formatVariance } from '../../utils/format';

interface AddBalanceItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  ingredients: DropdownItemOption[];
  currentBalances: InventoryBalanceItem[];
  warehouse: 'CENTRAL' | 'STORE';
  units: string[];
  onAdd: (item: {
    ingredient_name: string;
    actual_quantity: number;
    unit: string;
    current_quantity?: number;
  }) => void;
}

export const AddBalanceItemModal: React.FC<AddBalanceItemModalProps> = ({
  isOpen,
  onClose,
  ingredients,
  currentBalances,
  warehouse,
  units,
  onAdd,
}) => {
  const [selectedName, setSelectedName] = useState<string>('');
  const [actualQty, setActualQty] = useState<number>(0);
  const [unit, setUnit] = useState<string>('g');

  useEffect(() => {
    if (isOpen) {
      setSelectedName('');
      setActualQty(0);
      setUnit('g');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  const curBalance = currentBalances.find(
    (c) =>
      c.warehouse === warehouse &&
      c.ingredient_name.toLowerCase() === selectedName.trim().toLowerCase()
  );
  const sysQty = curBalance ? curBalance.quantity : 0;

  const handleSelectIngredient = (name: string, defaultUnit?: string) => {
    setSelectedName(name);
    const balance = currentBalances.find(
      (c) =>
        c.warehouse === warehouse &&
        c.ingredient_name.toLowerCase() === name.trim().toLowerCase()
    );
    const meta = ingredients.find(
      (i) => i.name.toLowerCase() === name.trim().toLowerCase()
    );
    const resolvedUnit = balance?.unit || meta?.unit || defaultUnit || 'g';
    setUnit(resolvedUnit);
    if (balance) {
      setActualQty(balance.quantity);
    } else {
      setActualQty(0);
    }
  };

  const currentCount = Math.max(0, actualQty);
  const isValid = Boolean(selectedName.trim());
  const diff = currentCount - sysQty;

  const handleConfirmAdd = () => {
    if (!isValid) return;
    tg.haptic.success();
    onAdd({
      ingredient_name: selectedName.trim(),
      actual_quantity: currentCount,
      unit: unit.trim() || 'g',
      current_quantity: sysQty,
    });
    onClose();
  };

  const handleQuickAddQty = (delta: number) => {
    tg.haptic.impact('light');
    setActualQty((prev) => Math.max(0, prev + delta));
  };

  return createPortal(
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9990,
        backgroundColor: 'rgba(21, 17, 14, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        className="modal-sheet"
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          backgroundColor: 'var(--bg-surface)',
          borderRadius: '20px 20px 0 0',
          padding: '16px 20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          boxShadow: 'var(--shadow-lg)',
          overflowY: 'auto',
          animation: 'slideUp 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Handle */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'var(--border-color)',
            }}
          />
        </div>

        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--primary-accent-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--primary-accent)',
              }}
            >
              <Scale size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>
                Thêm Món Kiểm Kê Kho
              </h3>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {warehouse === 'STORE' ? 'Kiểm kê tại Quầy Bar' : 'Kiểm kê tại Kho Tổng'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="stepper-btn"
            style={{ width: 32, height: 32, padding: 0, color: 'var(--text-secondary)' }}
            title="Đóng"
          >
            <X size={18} />
          </button>
        </div>

        {/* 1. Chọn nguyên liệu */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            1. Chọn nguyên liệu kiểm kê (*)
          </label>
          <ItemDropdown
            value={selectedName}
            items={ingredients}
            placeholder="🔍 Bấm để tìm & chọn nguyên liệu..."
            onChange={handleSelectIngredient}
            style={{ minHeight: 44, height: 44, fontSize: 14 }}
          />

          {selectedName && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, paddingLeft: 2 }}>
              Tồn trên máy: <b style={{ color: 'var(--primary-accent)' }}>{formatQty(sysQty, unit)}</b>
            </div>
          )}
        </div>

        {/* 2. Số lượng đếm thực tế & ĐVT */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            2. Số lượng đếm thực tế (*)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                border: '1.5px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-surface)',
                overflow: 'hidden',
                flex: 1,
                minHeight: 46,
              }}
            >
              <button
                type="button"
                onClick={() => handleQuickAddQty(-1)}
                className="stepper-btn"
                style={{ width: 44, height: 44, fontSize: 20 }}
                title="Giảm 1"
              >
                <Minus size={18} />
              </button>

              <input
                type="number"
                min="0"
                step="any"
                className="num"
                value={actualQty}
                onChange={(e) => setActualQty(Math.max(0, Number(e.target.value)))}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: 18,
                  color: 'var(--text-primary)',
                  backgroundColor: 'transparent',
                  padding: '4px',
                }}
              />

              <button
                type="button"
                onClick={() => handleQuickAddQty(1)}
                className="stepper-btn"
                style={{ width: 44, height: 44, fontSize: 20 }}
                title="Tăng 1"
              >
                <Plus size={18} />
              </button>
            </div>

            <div style={{ width: 85 }}>
              <UnitDropdown
                value={unit}
                units={units}
                onChange={(newUnit) => setUnit(newUnit)}
                style={{ minHeight: 46, height: 46 }}
              />
            </div>
          </div>

          {/* Quick presets */}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {[1, 5, 10, 50, 100].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => {
                  tg.haptic.impact('light');
                  setActualQty(preset);
                }}
                style={{
                  flex: 1,
                  padding: '5px 0',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-color)',
                  backgroundColor: actualQty === preset ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                  color: actualQty === preset ? '#FFFFFF' : 'var(--text-secondary)',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {preset}
              </button>
            ))}
          </div>
        </div>

        {/* Live difference preview */}
        {selectedName && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: diff === 0 ? 'var(--color-success-bg)' : 'var(--color-warning-bg)',
              border: `1px solid ${diff === 0 ? 'var(--color-success)' : 'var(--color-warning)'}`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
            }}
          >
            {diff === 0 ? (
              <>
                <CheckCircle2 size={16} color="var(--color-success)" style={{ flexShrink: 0 }} />
                <span style={{ fontWeight: 700, color: 'var(--color-success)' }}>
                  Khớp hoàn toàn với sổ sách trên máy ({formatQty(sysQty, unit)})
                </span>
              </>
            ) : (
              <>
                <AlertTriangle size={16} color="var(--color-warning)" style={{ flexShrink: 0 }} />
                <span style={{ fontWeight: 700, color: 'var(--color-warning)' }}>
                  Chênh lệch: {formatVariance(diff, unit)} so với sổ sách ({formatQty(sysQty, unit)})
                </span>
              </>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            disabled={!isValid}
            onClick={handleConfirmAdd}
            className="btn btn-primary btn-full"
            style={{
              minHeight: 48,
              fontSize: 15,
              fontWeight: 700,
              gap: 8,
              opacity: isValid ? 1 : 0.5,
              cursor: isValid ? 'pointer' : 'not-allowed',
            }}
          >
            <Plus size={18} /> Thêm Vào Cuối Danh Sách
          </button>

          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary btn-full"
            style={{ minHeight: 42, fontSize: 13, fontWeight: 600 }}
          >
            Hủy Bỏ
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
